import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import { useDispatch, useSelector } from 'react-redux'
import { Dialog, MenuItem, Select } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import {
  createEstateRoutine,
  updateEstateRoutine,
  attachEstatePortfolioRoutine
} from 'ducks/estates/actions'
import { selectEstatesSaving } from 'ducks/estates/selectors'
import { listPortfoliosRoutine } from 'ducks/portfolio/actions'
import { selectPortfolioList } from 'ducks/portfolio/selectors'
import { searchAssignableUsers } from 'services/taskService'
import { getActiveBuildingPassports } from 'services/analyticsService'
import { createPortfolio } from 'services/portfolioService'
import { hideEmail, filterOutStaff } from 'utils/user'
import { font, ink, labelStyle, line, radius, surface, transition } from 'features/dashboard/tokens'

const NEW_PORTFOLIO = '__new_portfolio__'

const userLabel = user => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
  return user.email ? `${name} (${hideEmail(user.email)})` : name || 'User'
}

const EstateEditorModal = ({ open, estate, onClose }) => {
  const dispatch = useDispatch()
  const portfolios = useSelector(selectPortfolioList)
  const saving = useSelector(selectEstatesSaving)

  const [name, setName] = useState(estate?.name || '')
  const [description, setDescription] = useState(estate?.description || '')
  const [leadUserId, setLeadUserId] = useState(estate?.lead?.id || '')
  const [portfolioId, setPortfolioId] = useState(estate?.portfolio?.id || '')
  const [selectedBps, setSelectedBps] = useState(estate?.buildingPassports || [])
  const [users, setUsers] = useState([])
  const [buildings, setBuildings] = useState([])
  const [search, setSearch] = useState('')
  const [creatingPortfolio, setCreatingPortfolio] = useState(false)
  const [newPortfolioName, setNewPortfolioName] = useState('')
  const [portfolioBusy, setPortfolioBusy] = useState(false)

  useEffect(() => {
    dispatch(listPortfoliosRoutine())
  }, [dispatch])

  const handleCreatePortfolio = async () => {
    const trimmed = newPortfolioName.trim()
    if (!trimmed || portfolioBusy) return
    setPortfolioBusy(true)
    try {
      const res = await createPortfolio({ name: trimmed })
      const created = res?.data?.data
      dispatch(listPortfoliosRoutine())
      if (created?.id) setPortfolioId(created.id)
      toast.success('Portfolio created.')
      setCreatingPortfolio(false)
      setNewPortfolioName('')
    } catch (error) {
      toast.error('Could not create the portfolio.')
    } finally {
      setPortfolioBusy(false)
    }
  }

  const handlePortfolioChange = value => {
    if (value === NEW_PORTFOLIO) {
      setCreatingPortfolio(true)
      return
    }
    setPortfolioId(value)
  }

  useEffect(() => {
    let active = true
    searchAssignableUsers({ search: '' })
      .then(res => {
        if (!active) return
        const fetched = filterOutStaff(res.data?.data || [])
        if (estate?.lead && !fetched.some(user => user.id === estate.lead.id)) {
          fetched.unshift(estate.lead)
        }
        setUsers(fetched)
      })
      .catch(() => {})
    getActiveBuildingPassports()
      .then(res => {
        if (active) setBuildings(res.data?.data || [])
      })
      .catch(() => {})
    return () => {
      active = false
    }
  }, [estate])

  const selectedIds = useMemo(() => selectedBps.map(bp => bp.id), [selectedBps])

  const availableBps = useMemo(() => {
    const query = search.trim().toLowerCase()
    return buildings
      .filter(bp => !selectedIds.includes(bp.id))
      .filter(bp => !query || (bp.name || '').toLowerCase().includes(query))
  }, [buildings, selectedIds, search])

  const handleAdd = bp => setSelectedBps(prev => [...prev, { id: bp.id, name: bp.name }])

  const handleRemove = bpId => setSelectedBps(prev => prev.filter(bp => bp.id !== bpId))

  const handleSave = () => {
    const trimmedName = name.trim()
    if (!trimmedName || !portfolioId) return

    if (estate?.id) {
      const initialIds = (estate.buildingPassports || []).map(bp => bp.id)
      const added = selectedIds.filter(id => !initialIds.includes(id))
      const removed = initialIds.filter(id => !selectedIds.includes(id))
      dispatch(
        updateEstateRoutine({
          id: estate.id,
          name: trimmedName,
          description: description.trim() || null,
          leadUserId: leadUserId || null,
          buildingPassportIds: added,
          detachBpIds: removed
        })
      )
      const initialPortfolioId = estate.portfolioId || ''
      if (portfolioId !== initialPortfolioId) {
        dispatch(attachEstatePortfolioRoutine({ id: estate.id, portfolioId }))
      }
    } else {
      dispatch(
        createEstateRoutine({
          name: trimmedName,
          description: description.trim() || null,
          leadUserId: leadUserId || null,
          buildingPassportIds: selectedIds,
          portfolioId
        })
      )
    }
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <Container>
        <Title>{estate?.id ? 'Edit estate' : 'New estate'}</Title>

        <TopRow>
          <Field $grow={2}>
            <FieldLabel>Name</FieldLabel>
            <TextInput
              data-tour-id='estateEditor.name'
              value={name}
              onChange={event => setName(event.target.value)}
              placeholder='Estate name'
            />
          </Field>
          <Field>
            <FieldLabel>Estate lead</FieldLabel>
            <Select
              value={leadUserId}
              onChange={event => setLeadUserId(event.target.value)}
              displayEmpty
              size='small'
              fullWidth
            >
              <MenuItem value=''>No lead</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {userLabel(user)}
                </MenuItem>
              ))}
            </Select>
          </Field>
          <Field>
            <FieldLabel>Portfolio</FieldLabel>
            {creatingPortfolio ? (
              <PortfolioCreateRow>
                <TextInput
                  autoFocus
                  value={newPortfolioName}
                  onChange={event => setNewPortfolioName(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Enter') handleCreatePortfolio()
                    if (event.key === 'Escape') { setCreatingPortfolio(false); setNewPortfolioName('') }
                  }}
                  placeholder='New portfolio name'
                />
                <InlineButton
                  type='button'
                  onClick={handleCreatePortfolio}
                  disabled={portfolioBusy || !newPortfolioName.trim()}
                >
                  {portfolioBusy ? 'Adding…' : 'Add'}
                </InlineButton>
                <InlineGhostButton
                  type='button'
                  onClick={() => { setCreatingPortfolio(false); setNewPortfolioName('') }}
                >
                  Cancel
                </InlineGhostButton>
              </PortfolioCreateRow>
            ) : (
              <Select
                data-tour-id='estateEditor.portfolio'
                value={portfolioId}
                onChange={event => handlePortfolioChange(event.target.value)}
                displayEmpty
                size='small'
                fullWidth
              >
                <MenuItem value='' disabled>Select a portfolio</MenuItem>
                {portfolios.map(portfolio => (
                  <MenuItem key={portfolio.id} value={portfolio.id}>
                    {portfolio.name}
                  </MenuItem>
                ))}
                <MenuItem value={NEW_PORTFOLIO}>
                  <NewPortfolioOption><AddRoundedIcon style={{ fontSize: 16 }} /> Create new portfolio…</NewPortfolioOption>
                </MenuItem>
              </Select>
            )}
          </Field>
        </TopRow>

        <Field>
          <FieldLabel>Description</FieldLabel>
          <TextInput
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder='Optional description'
          />
        </Field>

        <PanesRow>
          <Pane>
            <PaneHeader>Your Building Passports</PaneHeader>
            <SearchInput
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder='Search your Building Passports…'
            />
            <PaneList>
              {availableBps.length === 0 && (
                <PaneEmpty>
                  {buildings.length === 0
                    ? 'No Building Passports available to add.'
                    : 'No Building Passports found.'}
                </PaneEmpty>
              )}
              {availableBps.map(bp => (
                <PaneRow key={bp.id}>
                  <PaneRowName title={bp.name}>{bp.name}</PaneRowName>
                  <RowButton
                    type='button'
                    aria-label={`Add ${bp.name}`}
                    onClick={() => handleAdd(bp)}
                  >
                    +
                  </RowButton>
                </PaneRow>
              ))}
            </PaneList>
          </Pane>

          <Pane>
            <PaneHeader>In this estate ({selectedBps.length})</PaneHeader>
            <PaneList>
              {selectedBps.length === 0 && (
                <PaneEmpty>No Building Passports in this estate yet.</PaneEmpty>
              )}
              {selectedBps.map(bp => (
                <PaneRow key={bp.id}>
                  <PaneRowName title={bp.name}>{bp.name}</PaneRowName>
                  <RowButton
                    type='button'
                    aria-label={`Remove ${bp.name}`}
                    onClick={() => handleRemove(bp.id)}
                  >
                    ×
                  </RowButton>
                </PaneRow>
              ))}
            </PaneList>
          </Pane>
        </PanesRow>

        <Footer>
          <CancelButton type='button' onClick={onClose}>
            Cancel
          </CancelButton>
          <SaveButton type='button' data-tour-id='estateEditor.save' onClick={handleSave} disabled={!name.trim() || !portfolioId || saving}>
            Save
          </SaveButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateEditorModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const TopRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Field = styled.div`
  flex: ${({ $grow }) => $grow || 1};
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`

const FieldLabel = styled.span`
  ${labelStyle}
`

const TextInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.base};
  font-size: ${font.body};
  outline: none;
  box-sizing: border-box;
  transition: ${transition};

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
  }

  &::placeholder {
    color: ${ink.mute};
  }
`

const PanesRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const Pane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.wash};
  padding: 12px;
  min-height: 240px;
`

const PaneHeader = styled.div`
  ${labelStyle}
`

const SearchInput = styled(TextInput)`
  background: ${surface.card};
`

const PaneList = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 240px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PaneEmpty = styled.div`
  padding: 8px 2px;
  font-size: ${font.small};
  color: ${ink.mute};
`

const PaneRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  border-radius: ${radius.sm};
  background: ${surface.card};
  border: 1px solid ${line.base};
`

const PaneRowName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${font.body};
  color: ${ink.base};
`

const RowButton = styled.button`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${line.strong};
  border-radius: ${radius.round};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.emphasis};
  line-height: 1;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  padding: 9px 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    color: ${ink.base};
  }
`

const SaveButton = styled.button`
  padding: 9px 22px;
  border: none;
  border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const PortfolioCreateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`

const InlineButton = styled.button`
  flex-shrink: 0;
  padding: 9px 14px;
  border: none;
  border-radius: ${radius.sm};
  background: ${({ theme }) => theme.colors.primary};
  color: #ffffff;
  font-size: ${font.small};
  font-weight: 700;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const InlineGhostButton = styled.button`
  flex-shrink: 0;
  padding: 9px 10px;
  border: none;
  background: transparent;
  color: ${ink.mute};
  font-size: ${font.small};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover {
    color: ${ink.base};
  }
`

const NewPortfolioOption = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
`
